import type { ControlDeviceDto } from '../request/control-device.dto';

export type PlayerDevice = {
  deviceId: string;
  label: string;
  kind: 'audioinput' | 'videoinput' | 'audiooutput';
};

export type PlayerDeviceSettings = {
  cameraDeviceId: string | null;
  microphoneDeviceId: string | null;
  audioOutputDeviceId: string | null;
  audioOutputLabel: string | null;
  echoCancellation: boolean;
  noiseSuppression: boolean;
  autoGainControl: boolean;
  pointerInputMode: 'off' | 'holdToPoint' | 'universal';
  pointerHighlightStyle: 'laser' | 'focus';
  pointerHighlightSize: 'small' | 'medium' | 'large';
};

export type PlayerDeviceInventory = {
  cameras: PlayerDevice[];
  microphones: PlayerDevice[];
  audioOutputs: PlayerDevice[];
  settings: PlayerDeviceSettings;
};

export type PlayerDto = {
  id: string;
  socketId: string;
  state?: Record<string, unknown> & {
    page: ControlDeviceDto['playType'];
  };
  entityType?: string;
  entityId?: string;
  name?: string;
  isConnected: boolean;
  // Indicates if the player's LiveKit client is connected
  isLivekitConnected?: boolean;
  type: PlayerType;
  validUntil: Date;
  createdAt: Date;
  updatedAt: Date;
  // Slide information from media-agent (for presentations)
  currentSlideIndex?: number;
  totalSlides?: number;
  speakerNotes?: string;
  /** Provisioning status for the player's primary VM */
  vmStatus?: string;
  /** Primary VM id (from provisioning summary) */
  vmId?: string;
  /** Azure deployment lifecycle: prepared vs deployed */
  vmActivationStatus?: string;
  /** VM ids on the player document */
  vms?: string[];
  /** Media-agent placeholder lifecycle */
  activationStatus?: 'prepared' | 'paired';
  /** Devices and selections last reported by this player tab. Absent after disconnect. */
  deviceInventory?: PlayerDeviceInventory;
  /** Remote speaker console: flow slots this contributor speaks in. */
  speakerSlotIds?: string[];
};

export const PLAYER_TYPES = [
  'stagePlayer',
  'onlinePlayer',
  'screenPublisher',
  'roomPlayer',
  'mediaAgent',
  'confidencePlayer',
  /** Contributor console opened from the contribution page. Never paired by code. */
  'remoteSpeakerPlayer',
  /** Camera facing the audience, shown only to remote speakers. */
  'houseCamPlayer',
] as const;
export type PlayerType = (typeof PLAYER_TYPES)[number];

export const PRESENTABLE_PLAYER_TYPES = ['screenPublisher'] as const;

export type PresentablePlayerType = (typeof PRESENTABLE_PLAYER_TYPES)[number];
export type PresentablePlayer = PlayerDto & { type: PresentablePlayerType };

export function isPresentablePlayer(player: PlayerDto): player is PresentablePlayer {
  return (PRESENTABLE_PLAYER_TYPES as readonly string[]).includes(player.type);
}

/** Players that publish video/audio tracks (stage player, media player) */
export const PUBLISHING_PLAYER_TYPES = ['stagePlayer', 'screenPublisher'] as const;

export function isPublishingPlayer(player: PlayerDto): boolean {
  return (PUBLISHING_PLAYER_TYPES as readonly string[]).includes(player.type);
}
